/**
 * F-90: export the current sheet as CSV with RFC-4180 quoting.
 * Reads exclusively through BoardDoc accessors.
 *
 * VALUES ONLY, deliberately: CSV has no notion of formatting, so cell
 * backgrounds (F-17-bg) and any future per-property format keys are ignored
 * here — a styled export is the XLSX exporter's job (F-91, P1).
 */
import { IMAGE_CELL_TEXT_PLACEHOLDER, isImageCellValue, type BoardDoc } from "@kola/shared";

import { columnLabel } from "../../core/ids.js";

/**
 * Fields starting with =, +, -, @ (or a stray tab/CR) execute as FORMULAS
 * when the CSV is opened in Excel/Sheets — a collaborator (or a machine
 * translation) writing "=HYPERLINK(...)" into a cell must not run code in the
 * exporting user's spreadsheet app. RFC-4180 quoting does NOT neutralize
 * this (Excel unescapes before formula detection); the standard mitigation is
 * prefixing a single quote, which spreadsheet apps treat as a text marker.
 */
function neutralizeFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/** Quote a field when it contains a delimiter, quote, or line break. */
export function csvQuote(value: string): string {
  const safe = neutralizeFormula(value);
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replaceAll('"', '""')}"`;
  }
  return safe;
}

export function sheetToCsv(board: BoardDoc, sheetId: string): string {
  const colIds = board.sortedColIds(sheetId);
  const rowIds = board.sortedRowIds(sheetId);

  const header = colIds
    .map((colId, i) => csvQuote(board.getColumn(sheetId, colId)?.name || columnLabel(i)))
    .join(",");

  const lines = [header];
  for (const rowId of rowIds) {
    lines.push(
      colIds
        .map((colId) => {
          // F-91: image cells export as an explicit marker — a silent "" would
          // give no indication the cell held anything. (getCellText stays ""
          // for images: it feeds C5 content hashing, not presentation.)
          const snapshot = board.getCell(sheetId, rowId, colId);
          const text =
            snapshot !== undefined && isImageCellValue(snapshot.v)
              ? IMAGE_CELL_TEXT_PLACEHOLDER
              : board.getCellText(sheetId, rowId, colId);
          return csvQuote(text);
        })
        .join(","),
    );
  }
  return `${lines.join("\r\n")}\r\n`;
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel detects UTF-8.
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
